import { api } from '@/config/api';

export interface Pipeline {
  _id: string;
  pipelineUid: string;
  companyUid: string;
  name: string;
  description?: string;
  isDefault: boolean;
  sortOrder: number;
  createdAt: string;
}

export interface PipelineStage {
  _id: string;
  stageUid: string;
  companyUid: string;
  pipelineUid: string;
  name: string;
  order: number;
  terminal: 'active' | 'won' | 'lost';
  probability: number;
  rotDays?: number;
  color?: string;
}

export interface Deal {
  _id: string;
  dealUid: string;
  companyUid: string;
  pipelineUid: string;
  stageUid: string;
  title: string;
  description?: string;
  contactUid?: string;
  leadUid?: string;
  ownerUserUid?: string;
  amount?: number;
  currency: string;
  expectedCloseDate?: string;
  tags: string[];
  customFields: Record<string, unknown>;
  wonAt?: string;
  lostAt?: string;
  lostReason?: string;
  lastActivityAt?: string;
  nextActivityAt?: string;
  nextActivityType?: 'call' | 'meeting' | 'email' | 'task' | 'follow_up';
  nextActivityNote?: string;
  createdAt: string;
  updatedAt: string;
}

export interface WinProbabilityFactor {
  label: string;
  delta: number;
  reason: string;
}

export interface WinProbabilityResult {
  dealUid: string;
  score: number;
  baseFromStage: number;
  factors: WinProbabilityFactor[];
}

export interface ActivitySummaryResult {
  dealUid: string;
  summary: string;
  bullets: string[];
  tokensIn?: number;
  tokensOut?: number;
}

export interface ForecastRow {
  currency: string | null;
  stageUid: string;
  stageName: string;
  terminal: 'active' | 'won' | 'lost';
  color?: string;
  probability: number;
  count: number;
  total: number;
  weighted: number;
}

const BASE = '/slighter_crm/deals';

export const dealsService = {
  // Pipelines
  listPipelines: async (): Promise<Pipeline[]> => (await api.get(`${BASE}/pipelines`)).data,
  createPipeline: async (p: { name: string; description?: string }): Promise<Pipeline> =>
    (await api.post(`${BASE}/pipelines`, p)).data,
  updatePipeline: async (uid: string, p: Partial<{ name: string; description: string; sortOrder: number }>): Promise<Pipeline> =>
    (await api.patch(`${BASE}/pipelines/${uid}`, p)).data,
  deletePipeline: async (uid: string): Promise<{ ok: boolean }> =>
    (await api.delete(`${BASE}/pipelines/${uid}`)).data,

  // Stages
  listStages: async (pipelineUid: string): Promise<PipelineStage[]> =>
    (await api.get(`${BASE}/pipelines/${pipelineUid}/stages`)).data,
  createStage: async (pipelineUid: string, p: {
    name: string; order: number;
    terminal?: 'active' | 'won' | 'lost';
    probability?: number; rotDays?: number; color?: string;
  }): Promise<PipelineStage> => (await api.post(`${BASE}/pipelines/${pipelineUid}/stages`, p)).data,
  updateStage: async (stageUid: string, p: Partial<{
    name: string; order: number;
    terminal: 'active' | 'won' | 'lost';
    probability: number; rotDays: number; color: string;
  }>): Promise<PipelineStage> => (await api.patch(`${BASE}/stages/${stageUid}`, p)).data,
  deleteStage: async (stageUid: string): Promise<{ ok: boolean }> =>
    (await api.delete(`${BASE}/stages/${stageUid}`)).data,
  forecast: async (pipelineUid: string): Promise<ForecastRow[]> =>
    (await api.get(`${BASE}/pipelines/${pipelineUid}/forecast`)).data,

  // Deals
  list: async (params: {
    pipelineUid?: string; stageUid?: string; ownerUserUid?: string; contactUid?: string; leadUid?: string;
    q?: string; page?: number; limit?: number; currency?: string;
    closeFrom?: string; closeTo?: string; includeActive?: boolean;
  } = {}): Promise<{ items: Deal[]; total: number; page: number; limit: number }> => {
    const { includeActive, ...rest } = params;
    const query: Record<string, unknown> = { ...rest };
    if (includeActive !== undefined) query.includeActive = includeActive ? 'true' : 'false';
    return (await api.get(BASE, { params: query })).data;
  },

  get: async (uid: string): Promise<Deal> => (await api.get(`${BASE}/${uid}`)).data,

  create: async (payload: {
    title: string; description?: string;
    pipelineUid?: string; stageUid?: string;
    contactUid?: string; leadUid?: string; ownerUserUid?: string;
    amount?: number; currency?: string; expectedCloseDate?: string;
    tags?: string[]; customFields?: Record<string, unknown>;
  }): Promise<Deal> => (await api.post(BASE, payload)).data,

  update: async (uid: string, payload: Partial<Deal>): Promise<Deal> =>
    (await api.patch(`${BASE}/${uid}`, payload)).data,

  moveStage: async (uid: string, stageUid: string, lostReason?: string): Promise<Deal> =>
    (await api.post(`${BASE}/${uid}/move-stage`, { stageUid, lostReason })).data,

  win: async (uid: string): Promise<Deal> => (await api.post(`${BASE}/${uid}/win`)).data,
  lose: async (uid: string, lostReason: string): Promise<Deal> =>
    (await api.post(`${BASE}/${uid}/lose`, { lostReason })).data,

  remove: async (uid: string): Promise<{ ok: boolean }> => (await api.delete(`${BASE}/${uid}`)).data,

  winProbability: async (uid: string): Promise<WinProbabilityResult> =>
    (await api.get(`/slighter_crm/deals/deals/${uid}/win-probability`)).data,

  activitySummary: async (uid: string, payload: { provider?: string; model?: string } = {}): Promise<ActivitySummaryResult> =>
    (await api.post(`/slighter_crm/deals/deals/${uid}/activity-summary`, payload)).data,

  emails: async (uid: string): Promise<DealEmailThread> =>
    (await api.get(`${BASE}/${uid}/emails`)).data,
};

export interface DealEmailMessage {
  messageUid?: string;
  eventUid?: string;
  direction: 'inbound' | 'outbound';
  toEmail?: string;
  fromEmail?: string;
  from?: string;
  subject?: string;
  bodyHtml?: string;
  snippet?: string;
  status?: string;
  sentAt?: string;
  openedAt?: string;
  clickedAt?: string;
  openCount?: number;
  clickCount?: number;
  createdAt: string;
}

export interface DealEmailThread {
  outbound: DealEmailMessage[];
  inbound: DealEmailMessage[];
}
